import { useState, useEffect, useRef } from 'react';
import * as mediapipe from '@mediapipe/tasks-vision';

const useMediaPipeModels = (options = {}) => {
    // Configuration options with defaults
    const {
        faceDetectionConfidence = 0.6,
        poseDetectionConfidence = 0.6,
        handDetectionConfidence = 0.6,
        trackingConfidence = 0.3,
        loadFace = true,
        loadPose = true,
        loadHand = true,
        useLiteModels = true,
        useGPU = true
    } = options;

    // States
    const [faceLandmarker, setFaceLandmarker] = useState(null);
    const [poseLandmarker, setPoseLandmarker] = useState(null);
    const [handLandmarker, setHandLandmarker] = useState(null);
    const [modelsLoading, setModelsLoading] = useState(true);
    const [loadingProgress, setLoadingProgress] = useState({
        face: false,
        pose: false,
        hand: false
    });

    // Store vision resolver in a ref to avoid re-resolving
    const visionRef = useRef(null);

    // Error handling state
    const [error, setError] = useState(null);

    useEffect(() => {
        // Cancel flags for cleanup
        let isMounted = true;

        const initializeMediaPipe = async () => {
            try {
                // Only resolve vision once
                if (!visionRef.current) {
                    visionRef.current = await mediapipe.FilesetResolver.forVisionTasks(
                        "https://cdn.jsdelivr.net/npm/@mediapipe/tasks-vision@latest/wasm"
                    );
                }

                const vision = visionRef.current;
                const delegate = useGPU ? "GPU" : "CPU";

                // Load models in parallel instead of sequentially
                const modelPromises = [];

                if (loadFace) {
                    const facePromise = mediapipe.FaceLandmarker.createFromOptions(vision, {
                        baseOptions: {
                            modelAssetPath: "https://storage.googleapis.com/mediapipe-models/face_landmarker/face_landmarker/float16/1/face_landmarker.task",
                            delegate
                        },
                        runningMode: "VIDEO",
                        outputFaceBlendshapes: true,
                        minFaceDetectionConfidence: faceDetectionConfidence,
                        minTrackingConfidence: trackingConfidence
                    }).then(model => {
                        if (isMounted) {
                            setFaceLandmarker(model);
                            setLoadingProgress(prev => ({ ...prev, face: true }));
                        }
                        return model;
                    });
                    modelPromises.push(facePromise);
                } else {
                    setLoadingProgress(prev => ({ ...prev, face: true }));
                }

                if (loadPose) {
                    const poseModelPath = useLiteModels
                        ? "https://storage.googleapis.com/mediapipe-models/pose_landmarker/pose_landmarker_lite/float16/1/pose_landmarker_lite.task"
                        : "https://storage.googleapis.com/mediapipe-models/pose_landmarker/pose_landmarker_full/float16/1/pose_landmarker_full.task";

                    const posePromise = mediapipe.PoseLandmarker.createFromOptions(vision, {
                        baseOptions: {
                            modelAssetPath: poseModelPath,
                            delegate
                        },
                        runningMode: "VIDEO",
                        minPoseDetectionConfidence: poseDetectionConfidence,
                        minTrackingConfidence: trackingConfidence
                    }).then(model => {
                        if (isMounted) {
                            setPoseLandmarker(model);
                            setLoadingProgress(prev => ({ ...prev, pose: true }));
                        }
                        return model;
                    });
                    modelPromises.push(posePromise);
                } else {
                    setLoadingProgress(prev => ({ ...prev, pose: true }));
                }

                if (loadHand) {
                    const handPromise = mediapipe.HandLandmarker.createFromOptions(vision, {
                        baseOptions: {
                            modelAssetPath: "https://storage.googleapis.com/mediapipe-models/hand_landmarker/hand_landmarker/float16/1/hand_landmarker.task",
                            delegate
                        },
                        runningMode: "VIDEO",
                        numHands: 2,
                        minHandDetectionConfidence: handDetectionConfidence,
                        minTrackingConfidence: trackingConfidence
                    }).then(model => {
                        if (isMounted) {
                            setHandLandmarker(model);
                            setLoadingProgress(prev => ({ ...prev, hand: true }));
                        }
                        return model;
                    });
                    modelPromises.push(handPromise);
                } else {
                    setLoadingProgress(prev => ({ ...prev, hand: true }));
                }

                // Wait for all models to load
                await Promise.all(modelPromises);

                if (isMounted) {
                    setModelsLoading(false);
                }
            } catch (error) {
                console.error('Error initializing MediaPipe:', error);
                if (isMounted) {
                    setError(error);
                    setModelsLoading(false);
                }
            }
        };

        initializeMediaPipe();

        // Cleanup function
        return () => {
            isMounted = false;
        };
    }, [
        faceDetectionConfidence,
        poseDetectionConfidence,
        handDetectionConfidence,
        trackingConfidence,
        loadFace,
        loadPose,
        loadHand,
        useLiteModels,
        useGPU
    ]);

    return {
        faceLandmarker,
        poseLandmarker,
        handLandmarker,
        modelsLoading,
        loadingProgress,
        error
    };
};

export default useMediaPipeModels;